// Billing — plan display + upgrade CTA + waitlist capture.
//
// GET /api/v1/billing/status → plan + alerts_enabled + show_hubspot_upsell_banner
// POST /api/v1/billing/upgrade → Stripe checkout session redirect URL
// POST /api/v1/public/waitlist → waitlist email capture

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { CreditCard, CheckCircle, Zap, Bell, AlertCircle } from 'lucide-react';

import { fetchBillingStatus, startBillingUpgrade, joinWaitlist } from '../api';

function PlanBadge({ plan }: { plan: string }) {
  return plan === 'alerts' ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-400">
      <Zap size={12} /> Alerts plan
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
      Free plan
    </span>
  );
}

interface BillingProps {
  sessionEmail?: string;
}

export function Billing({ sessionEmail }: BillingProps) {
  const billing = useQuery({
    queryKey: ['pipeline-rot', 'billing-status'],
    queryFn: fetchBillingStatus,
  });

  // Upgrade email — pre-filled from session email; user can override for a
  // different billing contact. The billing upgrade endpoint requires { email }.
  const [upgradeEmail, setUpgradeEmail] = useState(sessionEmail ?? '');

  const upgradeMutation = useMutation({
    mutationFn: () => startBillingUpgrade(upgradeEmail.trim()),
    onSuccess: ({ checkout_url }) => {
      window.location.href = checkout_url;
    },
  });

  // Waitlist capture state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | undefined>();

  const waitlistMutation = useMutation({
    mutationFn: () => joinWaitlist(waitlistEmail.trim(), 'billing-page'),
    onSuccess: () => {
      setWaitlistDone(true);
      setWaitlistError(undefined);
    },
    onError: (err: Error) => {
      setWaitlistError(err.message || 'Something went wrong. Try again.');
    },
  });

  const handleWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (waitlistEmail.trim()) waitlistMutation.mutate();
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <CreditCard size={20} className="text-muted-foreground" />
        <div>
          <h1 className="text-lg font-semibold text-foreground">Billing</h1>
          <p className="text-sm text-muted-foreground">Your current plan and upgrade options.</p>
        </div>
      </div>

      {billing.isLoading && <Skeleton className="h-40 w-full" />}
      {billing.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.08] px-4 py-3 text-sm text-destructive">
          <AlertCircle size={14} />
          Failed to load billing status. {(billing.error as Error).message}
        </div>
      )}

      {billing.data && (
        <>
          {/* Current plan card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Current plan</CardTitle>
                <PlanBadge plan={billing.data.plan} />
              </div>
              <CardDescription className="text-sm">
                {billing.data.plan === 'alerts'
                  ? 'You have full alert dispatch enabled. The Pipeline Rot scanner will notify your team when deals stall.'
                  : 'You are on the free plan. You can view your rot dashboard and configure thresholds, but alerts are not sent.'}
              </CardDescription>
            </CardHeader>
            {billing.data.plan === 'alerts' && (
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <CheckCircle size={15} />
                  Alert dispatch is active — your team will be notified when deals rot.
                </div>
              </CardContent>
            )}
          </Card>

          {/* Upgrade card — shown to free-tier tenants */}
          {billing.data.plan === 'free' && (
            <Card className="border-primary/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell size={16} className="text-primary" />
                  Upgrade to Pipeline Rot Alerts — $39/mo
                </CardTitle>
                <CardDescription className="text-sm">
                  Enable real-time Slack + email alerts when deals stall in your pipeline.
                  Unlimited integrations, all 5 pipeline stages, daily digest reports.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm text-foreground">
                  {[
                    'Slack + email alerts on deal rot detection',
                    'Per-stage threshold customization',
                    'Historical scan timeline',
                    'Up to 4 CRM integrations (HubSpot, Pipedrive, Zoho, Freshsales)',
                    'Daily digest report to managers',
                  ].map((feat) => (
                    <li key={feat} className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-primary flex-shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <div className="space-y-2">
                  <label className="block text-xs text-muted-foreground">
                    Billing email (for Stripe checkout)
                  </label>
                  <input
                    type="email"
                    required
                    value={upgradeEmail}
                    onChange={(e) => setUpgradeEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <Button
                  className="w-full gap-2"
                  onClick={() => upgradeMutation.mutate()}
                  disabled={upgradeMutation.isPending || !upgradeEmail.trim()}
                  style={{ boxShadow: '0 0 16px rgba(239,68,68,0.25)' }}
                >
                  <Zap size={15} />
                  {upgradeMutation.isPending ? 'Redirecting to checkout…' : 'Upgrade for $39/mo'}
                </Button>
                {upgradeMutation.isError && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle size={11} />
                    {(upgradeMutation.error as Error).message || 'Checkout failed. Please try again.'}
                  </p>
                )}
                <p className="text-xs text-muted-foreground text-center">
                  Powered by Stripe. Cancel any time. No setup fee.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Waitlist capture — always visible for free tenants who aren't ready to pay */}
          {billing.data.plan === 'free' && !waitlistDone && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Not ready to upgrade?</CardTitle>
                <CardDescription className="text-xs">
                  Join the interest list and we'll reach out when we add team pricing or annual plans.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleWaitlist} className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    size="sm"
                    disabled={waitlistMutation.isPending || !waitlistEmail.trim()}
                  >
                    {waitlistMutation.isPending ? 'Joining…' : 'Join waitlist'}
                  </Button>
                </form>
                {waitlistError && (
                  <p className="mt-2 text-xs text-destructive flex items-center gap-1">
                    <AlertCircle size={11} /> {waitlistError}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {waitlistDone && (
            <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/[0.08] px-4 py-3 text-sm text-green-400">
              <CheckCircle size={15} /> You're on the list. We'll reach out when new plans are available.
            </div>
          )}
        </>
      )}
    </div>
  );
}
